import { useMemo, useState } from 'react';
import { Store, Search, X, Check, Loader2, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useSkateShops } from '@/hooks/useSkateShops';
import { SKATE_SHOP_KIND, parseAddrCoord, type SkateShop } from '@/lib/types';
import { cn } from '@/lib/utils';

/**
 * Pick a sponsoring skate shop by searching the shop directory — no raw
 * coordinates. Emits the selected shop's coordinate (`30017:pubkey:d`) via
 * `onChange`, or `''` when cleared.
 */
export function CrewSponsorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (coord: string) => void;
}) {
  const { data: shops, isLoading } = useSkateShops(200);
  const [query, setQuery] = useState('');

  // Resolve the currently-selected coordinate back to a shop for the preview.
  const selected = useMemo(() => {
    const coord = parseAddrCoord(value);
    if (!coord) return null;
    return shops?.find((s) => s.pubkey === coord.pubkey && s.identifier === coord.identifier) ?? null;
  }, [value, shops]);

  const results = useMemo(() => {
    if (!shops) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? shops.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            s.location?.toLowerCase().includes(q),
        )
      : shops;
    return list.slice(0, 8);
  }, [shops, query]);

  const coordOf = (s: SkateShop) => `${SKATE_SHOP_KIND}:${s.pubkey}:${s.identifier}`;

  // A shop is selected but not yet resolvable (still loading, or off-relay):
  // keep the coordinate but show a neutral chip so the choice isn't lost.
  const hasValueButUnresolved = !!parseAddrCoord(value) && !selected;

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3">
        {selected ? (
          <>
            <Avatar className="h-10 w-10 rounded-lg border border-amber-500/40">
              <AvatarImage src={selected.images?.[0]} alt={selected.name} className="object-cover" />
              <AvatarFallback className="rounded-lg bg-amber-500/15 text-amber-300">
                <Store className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-amber-400/80">
                <Check className="h-3 w-3" /> Sponsor selected
              </p>
              <p className="truncate text-sm font-black text-white">{selected.name}</p>
              {selected.location && (
                <p className="truncate text-xs text-gray-400">{selected.location}</p>
              )}
            </div>
          </>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-gray-400">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : <Store className="h-4 w-4 text-amber-400" />}
            <span className="truncate">
              {hasValueButUnresolved ? 'Sponsor set (shop loading…)' : 'Sponsor set'}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-gray-400 transition-colors hover:border-red-500/40 hover:text-red-400"
          aria-label="Clear sponsor"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search skate shops by name or city…"
          className="bg-gray-800 border-gray-700 pl-9 text-sm"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 px-1 py-3 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading shops…
        </div>
      ) : results.length === 0 ? (
        <p className="px-1 py-3 text-sm text-gray-500">
          {query ? 'No shops match — try a different search.' : 'No shops found yet.'}
        </p>
      ) : (
        <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
          {results.map((s) => (
            <button
              key={coordOf(s)}
              type="button"
              onClick={() => onChange(coordOf(s))}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border border-gray-700 bg-gray-800/60 p-2 text-left transition-all hover:border-amber-500/40 hover:bg-amber-500/[0.06] active:scale-[0.99]',
              )}
            >
              <Avatar className="h-9 w-9 rounded-lg">
                <AvatarImage src={s.images?.[0]} alt={s.name} className="object-cover" />
                <AvatarFallback className="rounded-lg bg-amber-500/15 text-amber-300">
                  <Store className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{s.name}</p>
                {s.location && (
                  <p className="flex items-center gap-1 truncate text-xs text-gray-500">
                    <MapPin className="h-3 w-3 shrink-0" /> {s.location}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
